// 导出接口按 Blob 接收，因此 JSON/文本错误也必须先解码。
function readBody (body) {
  if (typeof Blob !== 'undefined' && body instanceof Blob) {
    if (typeof body.text === 'function') return body.text()
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => resolve(reader.result)
      reader.onerror = () => reject(new Error('无法读取导出失败信息'))
      reader.readAsText(body, 'UTF-8')
    })
  }
  return Promise.resolve(body)
}

function messageFrom (body) {
  if (typeof body === 'string') {
    try { return messageFrom(JSON.parse(body)) } catch (ignore) { }
    return body.trim() && !/^\s*</.test(body) ? body.trim() : ''
  }
  if (body && typeof body === 'object') {
    return [body.detail, body.msg, body.message, body.result].find(value => typeof value === 'string' && value.trim()) || ''
  }
  return ''
}

export function exportErrorMessage (error) {
  const response = error && error.response
  const fallback = error && error.code === 'ECONNABORTED'
    ? '导出等待超时，请稍后重试；如多次超时，请联系管理员检查压缩包。'
    : response ? '订单表格导出失败，请联系管理员查看日志。'
      : (error && error.message) || '网络连接失败，请稍后重试。'
  return readBody(response && response.data).then(body => messageFrom(body) || fallback).catch(() => fallback)
}

// 错误原因配套处理建议，未知错误不暴露堆栈、SQL 或服务器路径。
export function exportErrorAdvice (error) {
  return exportErrorMessage(error).then(detail => {
    const status = error && error.response && error.response.status
    let reason = '系统暂时无法生成这份订单表格。'
    let action = '请稍后点击“重导表格”重试。如仍失败，请将订单号和出错时间提供给管理员检查日志，无需重复提交订单。'
    if (status === 401 || /Token expired/i.test(detail)) {
      reason = '登录状态已失效，暂时无法导出。'
      action = '请重新登录后，再打开该订单导出表格。'
    } else if (status === 403) {
      reason = '当前账号没有导出这份订单的权限。'
      action = '请联系管理员检查账号权限和订单所属打印中心。'
    } else if (/缺少可用模型明细/.test(detail)) {
      reason = '该订单缺少已保存的模型明细，暂时无法生成表格。'
      action = '请联系管理员补齐历史订单数据后重试，无需重复提交订单。'
    } else if (/无法启动 7-Zip|seven-zip/i.test(detail)) {
      reason = '服务器的 RAR 读取工具暂时不可用。'
      action = '请联系管理员检查服务器上的 7-Zip 配置，恢复后再点“重导表格”。不需要重新上传订单。'
    } else if (/(?:合并|关联)订单.*不存在|关联.*缺失/.test(detail)) {
      reason = '合并订单引用的部分订单记录不存在，无法生成完整表格。'
      action = '请联系管理员核对关联订单记录，无需重复提交订单。'
    } else if (/订单不存在/.test(detail)) {
      reason = '这份订单已不存在，或者当前账号无法读取它。'
      action = '请刷新订单列表确认记录；如订单仍在，请将订单号提供给管理员检查。'
    } else if (/原始文件不存在|NoSuchFile|找不到.*文件/i.test(detail)) {
      reason = '生成表格所需的原始模型文件在服务器上找不到。'
      action = '请将订单号及相关文件提供给管理员核查，无需重复提交订单。'
    } else if (/没有可用.*STL|没有可导出|表格为空/.test(detail)) {
      reason = '订单或其中的压缩包里没有找到可导出的 STL 模型文件。'
      action = '请检查对应压缩包是否包含 STL，而不只是图片或说明文件；确认后联系管理员补齐原订单的模型数据，再点“重导表格”。'
    } else if (/超过|安全限制/.test(detail)) {
      reason = '压缩包嵌套过深、文件太多或内层包太大，超过了安全处理范围。'
      action = '请联系管理员核对压缩包，必要时减少嵌套或拆分包并更新原订单文件，再点“重导表格”。'
    } else if (/超时|timeout/i.test(detail) || status === 408 || status === 504) {
      reason = '生成表格等待时间过长，本次未能取得完整表格。'
      action = '请稍后重试一次；如仍超时，请联系管理员检查压缩包大小和服务器负载。无需重复提交订单。'
    } else if (/正在更新|正在处理|事务尚未完成|待更新/.test(detail)) {
      reason = '订单文件正在更新或处理中，暂时不能导出。'
      action = '请等待文件更新结束，刷新订单列表后再点“重导表格”。'
    } else if (/加密|损坏|校验失败|zip end|zip file|invalid|malformed|读取失败|编码无法识别/i.test(detail)) {
      reason = '有压缩包无法读取，可能已损坏、设有密码或格式不受支持。'
      action = '请先在电脑上测试该压缩包能否完整解压；如有密码请去除密码。如无法解压，请向客户索取完整文件，由管理员更新原订单后重导。'
    } else if (!(error && error.response) && /network|网络|failed to fetch/i.test(detail)) {
      reason = '网络连接中断，未能取得表格。'
      action = '请检查网络连接后重试；如果其他页面也无法打开，请联系管理员检查服务是否正常。'
    }
    const candidates = detail.split(/[：；\r\n>]/).map(part => part.trim().split(/[\\/]/).pop())
    const file = candidates.filter(part => /\.(zip|rar|stl)$/i.test(part)).join(' → ').slice(0, 240)
    const missingOrder = detail.match(/(?:合并订单不存在|订单缺少打印文件记录)[：:]([^；\r\n]+)/)
    if (missingOrder) reason += ' 缺失数据的订单：' + missingOrder[1].trim() + '。'
    const match = detail.match(/文件所属订单：([^；\r\n]+)/)
    return { reason, action, file, sourceOrder: match ? match[1] : '' }
  })
}

export function showExportError (vm, error, orderCode) {
  return exportErrorAdvice(error || {}).then(advice => {
    const h = vm.$createElement
    const paragraph = (label, value) => h('p', { style: { margin: '10px 0', lineHeight: '1.7', wordBreak: 'break-word' } }, [h('strong', label), value])
    const content = [paragraph('问题说明：', advice.reason)]
    if (orderCode) content.push(paragraph('订单号：', String(orderCode)))
    if (advice.sourceOrder) content.push(paragraph('文件所属订单：', advice.sourceOrder))
    if (advice.file) content.push(paragraph('相关文件：', advice.file))
    content.push(paragraph('处理建议：', advice.action))
    return vm.$alert(h('div', content), '订单表格未能导出', { confirmButtonText: '我知道了', type: 'warning' })
  }).catch(() => {})
}

export function requireExportFile (response) {
  const body = response.data
  if (!body || body.size === 0) return Promise.reject(new Error('订单表格为空，请检查订单文件。'))
  const type = (body.type || (response.headers && response.headers['content-type']) || '').toLowerCase()
  if (/json|text\//.test(type)) {
    return readBody(body).then(data => { throw new Error(messageFrom(data) || '订单表格生成失败，服务端未返回Excel文件。') })
  }
  return Promise.resolve(response)
}


export function isOrderTableExportRequest (config) {
  return !!(config && /(?:^|\/)orderInfo\/exportBgR?(?:\?|$)/.test(config.url || ''))
}

// 两端统一下载与错误处理；缺失历史明细交由管理员处理，不发起恢复任务。
export async function exportOrderTable (vm, row, regenerate, client) {
  if (vm.progressVisible) return
  vm.progressVisible = true
  vm.progress = 0
  let cancelled = false
  const loading = vm.$loading ? vm.$loading({ lock: true, text: '正在准备表格，请稍候…' }) : null
  const cancel = () => { cancelled = true; if (loading) loading.close() }
  if (vm.$once) { vm.$once('hook:beforeDestroy', cancel); vm.$once('hook:deactivated', cancel) }
  try {
    const response = await client({ method: 'post', url: regenerate ? 'api/orderInfo/exportBgR' : 'api/orderInfo/exportBg',
      timeout: 300000, skipGlobalErrorMessage: true, responseType: 'blob', data: { id: row.id } }).then(requireExportFile)
    if (cancelled) return
    const fileName = row.orderCode + '-' + (row.techName || '') + '-订单数据.xlsx'
    if ('download' in document.createElement('a')) {
      const link = document.createElement('a')
      link.download = fileName
      link.style.display = 'none'
      const url = URL.createObjectURL(response.data)
      link.href = url
      document.body.appendChild(link)
      try { link.click() } finally { setTimeout(() => { URL.revokeObjectURL(url); document.body.removeChild(link) }, 100) }
    } else { navigator.msSaveBlob(response.data, fileName) }
    if (loading) loading.close()
    vm.progressVisible = false
  } catch (error) {
    if (loading) loading.close()
    vm.progressVisible = false
    if (!cancelled) await showExportError(vm, error, row.orderCode)
  } finally {
    if (loading) loading.close()
    if (vm.$off) { vm.$off('hook:beforeDestroy', cancel); vm.$off('hook:deactivated', cancel) }
    vm.progressVisible = false
  }
}



// WEBPACK FOOTER //
// ./src/assets/utils/orderTableExport.js